import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  PieChart, Pie, Cell, LineChart, Line, Area, AreaChart 
} from 'recharts';
import { 
  getSalesByStore, 
  getExpensesByCategory, 
  getProfitLossReport,
  getDashboardMetrics 
} from '../api/client';
import { 
  formatCurrency, 
  formatDate, 
  getCategoryLabel, 
  getChartColors,
  exportToCSV 
} from '../utils/formatters';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import StoreProfitability from './StoreProfitability';
import PaymentMethodsReport from './PaymentMethodsReport';
import SupplierPurchasesReport from './SupplierPurchasesReport';

const Reports = () => {
  const { user, isAdmin } = useAuth();
  const { companyName } = useTenant();
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    endDate: new Date()
  });
  
  const [reportData, setReportData] = useState({
    salesByStore: [],
    expensesByCategory: [],
    profitLoss: null,
    dashboardMetrics: null
  });

  const fetchReports = async () => {
    try {
      setLoading(true);
      
      // Check if dates are valid before calling toISOString()
      if (!dateRange.startDate || !dateRange.endDate) {
        console.warn('Invalid date range detected, skipping report fetch');
        setLoading(false);
        return;
      }
      
      const startDate = dateRange.startDate.toISOString().split('T')[0];
      const endDate = dateRange.endDate.toISOString().split('T')[0];

      const [salesByStore, expensesByCategory, profitLoss, dashboardMetrics] = await Promise.all([
        getSalesByStore(startDate, endDate),
        getExpensesByCategory(startDate, endDate),
        getProfitLossReport(startDate, endDate),
        getDashboardMetrics(startDate, endDate)
      ]);

      setReportData({
        salesByStore,
        expensesByCategory,
        profitLoss,
        dashboardMetrics
      });
      setError(null);
    } catch (err) {
      console.error('Error fetching reports:', err);
      setError('Error al cargar los reportes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [dateRange]);

  const handleDateChange = (field, date) => {
    setDateRange(prev => ({
      ...prev,
      [field]: date
    }));
  };

  const exportReport = (data, filename) => {
    exportToCSV(data, `${filename}_${formatDate(dateRange.startDate)}_${formatDate(dateRange.endDate)}`);
  };

  const colors = getChartColors();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
        <span className="ml-2 text-gray-600">Generando reportes...</span>
      </div>
    );
  }

  const { salesByStore, expensesByCategory, profitLoss, dashboardMetrics } = reportData;

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <img 
            src="/farmavida-logo.jpg" 
            alt={companyName} 
            className="h-12 w-auto object-contain"
          />
          <div>
            <h1 className="text-3xl font-bold text-blue-900">{`Reportes ${companyName}`}</h1>
            <p className="text-gray-600 mt-1">
              {isAdmin 
                ? "Análisis completo de tu negocio farmacéutico - Vista Administrador"
                : `Reportes básicos disponibles - ${user?.full_name || user?.username}`
              }
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-4">
          {/* Role Badge */}
          <div className={`px-3 py-1 rounded-full text-xs font-medium ${
            isAdmin 
              ? 'bg-red-100 text-red-800' 
              : 'bg-green-100 text-green-800'
          }`}>
            {isAdmin ? 'Admin - Acceso Completo' : 'Empleado - Acceso Limitado'}
          </div>
          <button onClick={fetchReports} className="btn-outline">
            🔄 Actualizar Reportes
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="card">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('general')}
            className={`px-6 py-3 text-sm font-medium ${
              activeTab === 'general'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            📊 Reportes Generales
          </button>
          
          {/* Employee can see store profitability but limited data */}
          <button
            onClick={() => setActiveTab('profitability')}
            className={`px-6 py-3 text-sm font-medium ${
              activeTab === 'profitability'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            🏆 Rentabilidad por Tienda
            {!isAdmin && (
              <span className="ml-1 text-xs bg-yellow-100 text-yellow-800 px-1 rounded">Limitado</span>
            )}
          </button>

          {/* Payment methods - Admin only */}
          {isAdmin && (
            <button
              onClick={() => setActiveTab('payment-methods')}
              className={`px-6 py-3 text-sm font-medium ${
                activeTab === 'payment-methods'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              💳 Métodos de Pago
              <span className="ml-1 text-xs bg-red-100 text-red-800 px-1 rounded">Admin</span>
            </button>
          )}

          {/* Supplier purchases - Admin only */}
          {isAdmin && (
            <button
              onClick={() => setActiveTab('supplier-purchases')}
              className={`px-6 py-3 text-sm font-medium ${
                activeTab === 'supplier-purchases'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              📦 Compras por Proveedor
              <span className="ml-1 text-xs bg-red-100 text-red-800 px-1 rounded">Admin</span>
            </button>
          )}
        </div>

        {/* Access Control Notice for Employees */}
        {!isAdmin && (
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 m-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-700">
                  <strong>Vista de Empleado:</strong> Acceso limitado a reportes básicos. 
                  Los reportes financieros detallados están disponibles solo para administradores.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tab Content */}
      {activeTab === 'general' ? (
        <GeneralReports 
          reportData={reportData}
          dateRange={dateRange}
          setDateRange={setDateRange}
          error={error}
          setError={setError}
          loading={loading}
          fetchReports={fetchReports}
        />
      ) : activeTab === 'profitability' ? (
        <StoreProfitability />
      ) : activeTab === 'payment-methods' ? (
        <PaymentMethodsReport />
      ) : activeTab === 'supplier-purchases' ? (
        <SupplierPurchasesReport />
      ) : null}
    </div>
  );
};

const GeneralReports = ({ reportData, dateRange, setDateRange, error, setError, loading, fetchReports }) => {
  const handleDateChange = (field, date) => {
    setDateRange(prev => ({
      ...prev,
      [field]: date
    }));
  };

  const exportReport = (data, filename) => {
    exportToCSV(data, `${filename}_${formatDate(dateRange.startDate)}_${formatDate(dateRange.endDate)}`);
  };

  const colors = getChartColors();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
        <span className="ml-2 text-gray-600">Generando reportes...</span>
      </div>
    );
  }

  const { salesByStore, expensesByCategory, profitLoss, dashboardMetrics } = reportData;

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Reportes y Análisis</h1>
          <p className="text-gray-600 mt-1">
            Análisis detallado del período: {formatDate(dateRange.startDate)} - {formatDate(dateRange.endDate)}
          </p>
        </div>
        <button onClick={fetchReports} className="btn-outline">
          🔄 Actualizar Reportes
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <p>{error}</p>
          <button onClick={() => setError(null)} className="text-sm underline ml-2">
            Cerrar
          </button>
        </div>
      )}

      {/* Date Range Selector */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">📅 Seleccionar Período</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="form-label">Fecha Desde</label>
            <DatePicker
              selected={dateRange.startDate}
              onChange={(date) => handleDateChange('startDate', date)}
              className="form-input"
              dateFormat="dd/MM/yyyy"
            />
          </div>
          <div>
            <label className="form-label">Fecha Hasta</label>
            <DatePicker
              selected={dateRange.endDate}
              onChange={(date) => handleDateChange('endDate', date)}
              className="form-input"
              dateFormat="dd/MM/yyyy"
            />
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {profitLoss && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="stat-card">
            <div className="stat-card-icon bg-green-500">💰</div>
            <div className="stat-card-value">{formatCurrency(profitLoss.revenue.total_sales)}</div>
            <div className="stat-card-label">Ventas Totales</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-icon bg-red-500">📊</div>
            <div className="stat-card-value">{formatCurrency(profitLoss.total_expenses)}</div>
            <div className="stat-card-label">Gastos Totales</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-icon bg-blue-500">📈</div>
            <div className="stat-card-value">{formatCurrency(profitLoss.gross_profit)}</div>
            <div className="stat-card-label">Ganancia Bruta</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-icon bg-purple-500">🎯</div>
            <div className={`stat-card-value ${profitLoss.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatCurrency(profitLoss.net_profit)}
            </div>
            <div className="stat-card-label">Ganancia Neta</div>
          </div>
        </div>
      )}

      {/* Sección eliminada - gráficos duplicados */}
      {profitLoss && (
        <div className="card">
          <div className="card-header flex justify-between items-center">
            <h3 className="card-title">📊 Estado de Resultados</h3>
            <button 
              onClick={() => exportReport([profitLoss], 'estado_resultados')}
              className="btn-outline text-sm"
            >
              📥 Exportar
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Ingresos */}
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-4">💰 Ingresos</h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                  <span className="text-gray-700">Ventas Totales</span>
                  <span className="font-bold text-green-600">
                    {formatCurrency(profitLoss.revenue.total_sales)}
                  </span>
                </div>
                {(profitLoss.cost_of_goods_sold !== undefined || profitLoss.inventory_cost !== undefined) && (
                  <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg">
                    <span className="text-gray-700">(-) Costo de lo Vendido</span>
                    <span className="font-bold text-orange-600">
                      {formatCurrency(profitLoss.cost_of_goods_sold || profitLoss.inventory_cost || 0)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between items-center p-3 bg-green-100 rounded-lg">
                  <span className="font-semibold text-gray-900">= Utilidad Bruta</span>
                  <span className="font-bold text-green-700">
                    {formatCurrency(profitLoss.gross_profit)}
                  </span>
                </div>
              </div>
            </div>

            {/* Gastos Operativos */}
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-4">📉 Gastos Operativos</h4>
              <div className="space-y-2">
                {Object.entries(profitLoss.expenses).map(([category, amount]) => (
                  <div key={category} className="flex justify-between items-center p-2 bg-red-50 rounded">
                    <span className="text-gray-700">{getCategoryLabel(category)}</span>
                    <span className="font-medium text-red-600">
                      {formatCurrency(amount)}
                    </span>
                  </div>
                ))}
                <div className="flex justify-between items-center p-3 bg-red-100 rounded-lg mt-3">
                  <span className="font-semibold text-gray-900">Total Gastos Operativos</span>
                  <span className="font-bold text-red-700">
                    {formatCurrency(profitLoss.total_expenses)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Resultados Finales */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {formatCurrency(profitLoss.gross_profit)}
                </div>
                <div className="text-sm text-gray-600">Ganancia Bruta</div>
              </div>
              <div className="text-center">
                <div className={`text-2xl font-bold ${profitLoss.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(profitLoss.net_profit)}
                </div>
                <div className="text-sm text-gray-600">Ganancia Neta</div>
              </div>
              <div className="text-center">
                <div className={`text-2xl font-bold ${profitLoss.profit_margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {profitLoss.profit_margin.toFixed(1)}%
                </div>
                <div className="text-sm text-gray-600">Margen de Ganancia</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ventas por Tienda - Gráfico Grande */}
      <div className="chart-container">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">🏪 Ventas por Tienda</h3>
          <button 
            onClick={() => exportReport(salesByStore, 'ventas_por_tienda')}
            className="btn-outline text-sm"
          >
            📥 Exportar
          </button>
        </div>
        {salesByStore && salesByStore.length > 0 ? (
          <ResponsiveContainer width="100%" height={500}>
            <BarChart 
              data={salesByStore.map(item => ({
                tienda: item.store_name,
                ventas: item.total_sales
              }))}
              margin={{ top: 20, right: 30, left: 20, bottom: 120 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="tienda" 
                angle={-45}
                textAnchor="end"
                height={120}
                interval={0}
                fontSize={12}
                tick={{ fontSize: 12 }}
              />
              <YAxis 
                tickFormatter={(value) => formatCurrency(value)}
                width={80}
              />
              <Tooltip 
                formatter={(value) => [formatCurrency(value), 'Ventas']}
              />
              <Bar dataKey="ventas" fill={colors[0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No hay datos de ventas para este período
          </div>
        )}
      </div>

      {/* Gastos por Categoría - Separado */}
      <div className="chart-container">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">📊 Gastos por Categoría</h3>
          <button 
            onClick={() => exportReport(expensesByCategory, 'gastos_por_categoria')}
            className="btn-outline text-sm"
          >
            📥 Exportar
          </button>
        </div>
        {expensesByCategory && expensesByCategory.length > 0 ? (
          <ResponsiveContainer width="100%" height={400}>
            <PieChart>
              <Pie
                data={expensesByCategory.map(item => ({
                  categoria: getCategoryLabel(item.category),
                  gastos: item.total_amount
                }))}
                cx="50%"
                cy="50%"
                outerRadius={130}
                dataKey="gastos"
                label={({ categoria, gastos }) => `${categoria}: ${formatCurrency(gastos)}`}
              >
                {expensesByCategory.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => formatCurrency(value)} />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No hay datos de gastos para este período
          </div>
        )}
      </div>
    </div>
  );
};

export default Reports;